/* Narrator keyboard shortcuts — user-configurable bindings.
 *
 * Stored in localStorage as JSON under NARRATOR_SHORTCUTS_KEY.
 * Each binding: { action: string, ctrl: bool, alt: bool, shift: bool, meta: bool, key: string }
 *
 * Convention:
 *   - `key` is a single lowercase character ("l", "1", "arrowleft") matching e.key.toLowerCase()
 *   - Meta and Ctrl are unified (Cmd on Mac = Ctrl on Win/Linux)
 */

export const NARRATOR_SHORTCUTS_KEY = 'narrator_shortcuts_v1';

// ============== ACTIONS ==============
export const NARRATOR_ACTIONS = [
  { id: 'toggle',       label: 'Slå av/på Berättaren',          emoji: '🎙️', global: true },
  { id: 'mode-sentence',label: 'Läs mening-för-mening',          emoji: '📜', global: true },
  { id: 'mode-word',    label: 'Läs ord-för-ord',                emoji: '💬', global: true },
  { id: 'read-selected',label: 'Läs upp markerad text',          emoji: '🔊', global: true },
  { id: 'pause-resume', label: 'Pausa / fortsätt uppläsning',    emoji: '⏯️', global: true },
  { id: 'stop',         label: 'Stoppa uppläsning',              emoji: '⏹️', global: true },
  { id: 'speed-up',     label: 'Snabbare uppläsning',            emoji: '⏩', global: true },
  { id: 'speed-down',   label: 'Långsammare uppläsning',         emoji: '⏪', global: true },
  { id: 'repeat-last',  label: 'Upprepa sista meningen',         emoji: '🔁', global: true },
  { id: 'open-panel',   label: 'Öppna Berättare-panelen',        emoji: '🎛️', global: true },
  { id: 'show-cheats',  label: 'Visa alla genvägar (cheat sheet)', emoji: '🎹', global: true },
];

// ============== DEFAULT BINDINGS ==============
export const DEFAULT_SHORTCUTS = {
  'toggle':        { ctrl: true, alt: true, shift: false, meta: false, key: 'l' },
  'mode-sentence': { ctrl: true, alt: true, shift: false, meta: false, key: 's' },
  'mode-word':     { ctrl: true, alt: true, shift: false, meta: false, key: 'w' },
  'read-selected': { ctrl: true, alt: true, shift: false, meta: false, key: 'r' },
  'pause-resume':  { ctrl: true, alt: true, shift: false, meta: false, key: 'p' },
  'stop':          { ctrl: true, alt: true, shift: false, meta: false, key: 'x' },
  'speed-up':      { ctrl: true, alt: true, shift: false, meta: false, key: '+' },
  'speed-down':    { ctrl: true, alt: true, shift: false, meta: false, key: '-' },
  'repeat-last':   { ctrl: true, alt: true, shift: false, meta: false, key: 'b' },
  'open-panel':    { ctrl: true, alt: true, shift: true,  meta: false, key: 'l' },
  'show-cheats':   { ctrl: true, alt: true, shift: false, meta: false, key: '/' },
};

// ============== LOAD / SAVE ==============
export function loadShortcuts() {
  try {
    const raw = localStorage.getItem(NARRATOR_SHORTCUTS_KEY);
    if (!raw) return { ...DEFAULT_SHORTCUTS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_SHORTCUTS, ...parsed };
  } catch {
    return { ...DEFAULT_SHORTCUTS };
  }
}

export function saveShortcuts(map) {
  try { localStorage.setItem(NARRATOR_SHORTCUTS_KEY, JSON.stringify(map)); } catch {/**/}
  // Notify listeners (NarratorPanel + GlobalNarrator) so they reload
  try { window.dispatchEvent(new CustomEvent('narrator-shortcuts-changed')); } catch {/**/}
}

export function resetShortcuts() {
  saveShortcuts({ ...DEFAULT_SHORTCUTS });
  return { ...DEFAULT_SHORTCUTS };
}

// ============== FORMATTING ==============
const KEY_LABEL = {
  ' ': 'Mellanslag',
  'arrowleft': '←',
  'arrowright': '→',
  'arrowup': '↑',
  'arrowdown': '↓',
  'enter': 'Enter',
  'escape': 'Esc',
  'tab': 'Tab',
  'backspace': '⌫',
};

export function formatBinding(b) {
  if (!b || !b.key) return '— ingen —';
  const parts = [];
  if (b.ctrl || b.meta) parts.push('Ctrl');
  if (b.alt) parts.push('Alt');
  if (b.shift) parts.push('Shift');
  let k = (b.key || '').toLowerCase();
  k = KEY_LABEL[k] || k.toUpperCase();
  parts.push(k);
  return parts.join('+');
}

// ============== MATCH ==============
// True if a KeyboardEvent matches the binding
export function matchBinding(e, b) {
  if (!e || !b || !b.key) return false;
  const ctrl = !!(e.ctrlKey || e.metaKey);
  const alt  = !!e.altKey;
  const shift = !!e.shiftKey;
  const wantCtrl = !!(b.ctrl || b.meta);
  if (wantCtrl !== ctrl) return false;
  if (!!b.alt !== alt) return false;
  if (!!b.shift !== shift) return false;
  const k = (e.key || '').toLowerCase();
  return k === (b.key || '').toLowerCase();
}

// Capture a key combo from a KeyboardEvent
export function captureBinding(e) {
  const ctrl = !!(e.ctrlKey || e.metaKey);
  const alt  = !!e.altKey;
  const shift = !!e.shiftKey;
  const k = (e.key || '').toLowerCase();
  // Ignore pure modifier presses
  if (['control', 'alt', 'shift', 'meta', 'os'].includes(k)) return null;
  // Require at least one modifier to avoid accidental hijacking of normal typing
  if (!ctrl && !alt && !['f1','f2','f3','f4','f5','f6','f7','f8','f9','f10','f11','f12','escape'].includes(k)) {
    return null;
  }
  return { ctrl, alt, shift, meta: false, key: k };
}

// Find which action (if any) matches an event given the current bindings
export function actionForEvent(e, bindings) {
  for (const [action, b] of Object.entries(bindings || {})) {
    if (matchBinding(e, b)) return action;
  }
  return null;
}
